import React from 'react';
import {
  addSignCommaFormat,
  commaFormat,
  getSign,
} from '../../utils/formatter';
import { Chart as Chart_ } from '../../components/ComparisonBetweenIndexAndAsset/HighChart';
import {
  ComparisonBetweenIndexAndAsset as ComparisonBetweenIndexAndAssetInternal,
  ComparisonBetweenIndexAndAssetProps as ComparisonBetweenIndexAndAssetProps_,
  Entries,
  apiKeyToName,
  INDEX_TYPE,
  WholeData,
  createMine,
  createNK225,
  createTOPIX,
  createDJI,
  createSSI,
  createUSDJPY,
  createBTCJPY,
} from '../../components/ComparisonBetweenIndexAndAsset/ComparisonBetweenIndexAndAsset';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { DATE_FORMAT } from 'components/features/new-report/utils/chartTypes';
import { GetIndexesItemResult } from 'config/api';

export type ComparisonBetweenIndexAndAssetProps = ComparisonBetweenIndexAndAssetProps_;

export const Chart = Chart_;

export const ComparisonBetweenIndexAndAsset = ({
  Indexes,
}: {
  Indexes: GetIndexesItemResult[];
}) => {
  const myAssets = Indexes.map(i => {
    return i.Mine;
  });
  const latestMyAsset =
    myAssets.length > 0 ? myAssets[myAssets.length - 1] : null;
  const indices = Indexes.map(data => {
    return {
      Day: data.Day,
      BTCJPY: data.BTCJPY,
      DJI: data.DJI,
      NK225: data.NK225,
      SSI: data.SSI,
      TOPIX: data.TOPIX,
      USDJPY: data.USDJPY,
    };
  });
  const latestIndex = indices[indices.length - 1];
  const latestDay =
    yyyymmddToDate(latestIndex?.Day.toString() || '') || new Date();
  const indiceEntries = indices.map(index => {
    return Object.entries(index).filter(e => {
      const [key] = e;
      return key !== 'Day';
    }) as Entries<Omit<typeof index, 'Day'>>;
  });
  const latestIndexEntry = indiceEntries[indiceEntries.length - 1];
  const rowData =
    latestIndexEntry?.map(data => {
      const [key, value] = data;
      const latestDateTime = value?.DateProperty
        ? new Date(value.DateProperty.replace(' ', 'T'))
        : latestDay;
      return {
        indexName: apiKeyToName(key),
        indexType: INDEX_TYPE[key],
        indexSign: getSign(value?.Rate ?? 0),
        id: key,
        indexChangeRatio: addSignCommaFormat((value?.Rate ?? 0) * 100, {
          numberOfDecimals: 2,
        }),
        indexValue: commaFormat(value?.Amount ?? 0, { numberOfDecimals: 2 }),
        indexValueDatetime: `${latestDay.getMonth() +
          1}/${latestDay.getDate()} ${latestDateTime
          .getHours()
          .toString()
          .padStart(2, '0')}:${latestDateTime
          .getMinutes()
          .toString()
          .padStart(2, '0')}`,
      };
    }) || [];
  const wholeData = Indexes.map(datum => {
    const ret: WholeData[number] = [
      yyyymmddToDate(datum.Day.toString()) ?? new Date(),
      datum.Mine?.Incorrect ?? false,
      createMine(datum.Mine?.Rate ?? 0),
      createNK225(datum.NK225?.Rate ?? 0),
      createTOPIX(datum.TOPIX?.Rate ?? 0),
      createDJI(datum.DJI?.Rate ?? 0),
      createSSI(datum.SSI?.Rate ?? 0),
      createUSDJPY(datum.USDJPY?.Rate ?? 0),
      createBTCJPY(datum.BTCJPY?.Rate ?? 0),
    ];
    return ret;
  });
  return (
    <ComparisonBetweenIndexAndAssetInternal
      {...{
        comparisonSign: getSign(latestMyAsset?.Rate ?? 0),
        comparisonPercentage: addSignCommaFormat(
          (latestMyAsset?.Rate ?? 0) * 100,
          {
            numberOfDecimals: 2,
          }
        ),
        valuation: commaFormat(latestMyAsset?.Amount ?? 0),
        latestMyAssetRate: latestMyAsset?.Rate ?? 0,
        rowData,
        wholeData,
        periodUnit: 'ヶ月',
        tickInterval: 7,
        dateFormat: DATE_FORMAT.WITHOUT_WEEKDAY,
        Chart,
      }}
    />
  );
};
